'use client';

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import MenuIcon from '@mui/icons-material/Menu';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Link from 'next/link';
import React, { useEffect, useState } from 'react';
import { motion, type Variants } from 'framer-motion';
import { portfolioContent, type PortfolioContent } from '@/lib/portfolio/content';

type ContactFormState = {
  name: string;
  email: string;
  company: string;
  projectType: string;
  budget: string;
  message: string;
};

type ChatMessage = {
  role: 'user' | 'bot';
  text: string;
};

const initialContactState: ContactFormState = {
  name: '',
  email: '',
  company: '',
  projectType: 'Technical Lead Role',
  budget: 'Full-time',
  message: '',
};

const fallbackPortfolio: PortfolioContent = portfolioContent;

const sectionReveal: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
  },
};

const fadeReveal: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  },
};

const bioStagger: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1,
    },
  },
};

const bioItem: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  },
};

const listStagger: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.12,
    },
  },
};

const navItems = [
  { label: 'Projects', href: '#projects' },
  { label: 'AI Apps', href: '#ai-projects' },
  { label: 'Services', href: '#services' },
  { label: 'Experience', href: '#experience' },
  { label: 'Contact', href: '#contact' },
  { label: 'Download Resume', href: '/SubhaChandraBose-Fullstack-TechLead-v2.pdf', isDownload: true },
] as const;

const HERO_VIDEO_SRC = '/subhash-coding-video.mp4';
const HERO_VIDEO_POSTER = '/subhash-herobanner.png';
const TONE_OPTIONS = ['Bold', 'Professional', 'Friendly'] as const;
const CHATBOT_WELCOME_MESSAGE =
  'Hi, I am your AI assistant. Ask about integrations, use cases, or deployment options.';

const buildAdCopy = (product: string, audience: string, tone: string) => {
  const cleanProduct = product.trim() || 'Your Product';
  const cleanAudience = audience.trim() || 'target users';
  return `${cleanProduct} helps ${cleanAudience} move faster with AI-powered workflows. Launch smarter campaigns with ${cleanProduct} and turn every interaction into measurable growth. Tone: ${tone}.`;
};

const getChatbotReply = (message: string) => {
  const normalized = message.toLowerCase();

  if (normalized.includes('price') || normalized.includes('pricing') || normalized.includes('cost')) {
    return 'Pricing can be usage-based or seat-based. I can help you model both options for your product.';
  }
  if (normalized.includes('integrat') || normalized.includes('api')) {
    return 'We support REST and webhook integrations. Typical onboarding takes 2-5 days including auth and testing.';
  }
  if (normalized.includes('deploy') || normalized.includes('production')) {
    return 'Production setup includes logging, rate limits, retry logic, and guardrails for safer responses.';
  }
  if (normalized.includes('chatbot') || normalized.includes('assistant')) {
    return 'Chat bots can be tailored for support, onboarding, and internal knowledge workflows with domain grounding.';
  }

  return 'Great question. For this scenario, I recommend starting with a scoped pilot and tracking response quality, speed, and conversion impact.';
};

function ContactForm() {
  const [formState, setFormState] = useState<ContactFormState>(initialContactState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<{ type: 'idle' | 'success' | 'error'; message: string }>({
    type: 'idle',
    message: '',
  });

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = event.target;
    setFormState((previous) => ({ ...previous, [name]: value }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus({ type: 'idle', message: '' });
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formState),
      });

      const data = (await response.json()) as {
        id?: string;
        message?: string;
        errors?: string[];
      };

      if (!response.ok) {
        throw new Error(data.errors?.[0] ?? data.message ?? 'Could not send your message.');
      }

      setStatus({
        type: 'success',
        message: `Message sent. Reference ID: ${data.id ?? 'created'}`,
      });
      setFormState(initialContactState);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Something went wrong.';
      setStatus({ type: 'error', message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldClassName = 'ai-input mt-1 w-full px-4 py-3 text-sm outline-none';

  return (
    <form onSubmit={handleSubmit} className="ai-card space-y-4 p-6 md:p-8">
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="text-sm font-medium text-zinc-300">
          Name
          <input
            className={fieldClassName}
            name="name"
            value={formState.name}
            onChange={handleChange}
            placeholder="Your name"
            required
            minLength={2}
            maxLength={80}
          />
        </label>

        <label className="text-sm font-medium text-zinc-300">
          Email
          <input
            className={fieldClassName}
            name="email"
            type="email"
            value={formState.email}
            onChange={handleChange}
            placeholder="you@company.com"
            required
          />
        </label>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="text-sm font-medium text-zinc-300">
          Company
          <input
            className={fieldClassName}
            name="company"
            value={formState.company}
            onChange={handleChange}
            placeholder="Optional"
            maxLength={80}
          />
        </label>

        <label className="text-sm font-medium text-zinc-300">
          Inquiry Type
          <select className={fieldClassName} name="projectType" value={formState.projectType} onChange={handleChange}>
            <option>Technical Lead Role</option>
            <option>Senior Full-Stack Role</option>
            <option>Contract Engagement</option>
            <option>Product Collaboration</option>
          </select>
        </label>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="text-sm font-medium text-zinc-300">
          Engagement
          <select className={fieldClassName} name="budget" value={formState.budget} onChange={handleChange}>
            <option>Full-time</option>
            <option>Contract</option>
            <option>Consulting</option>
            <option>Project-based</option>
          </select>
        </label>

        <label className="text-sm font-medium text-zinc-300">
          Timeline
          <input
            className={fieldClassName}
            name="timeline"
            value="Immediate / 2-4 weeks"
            readOnly
            aria-label="Availability timeline"
          />
        </label>
      </div>

      <label className="text-sm font-medium text-zinc-300">
        Message
        <textarea
          className={`${fieldClassName} min-h-32 resize-y`}
          name="message"
          value={formState.message}
          onChange={handleChange}
          placeholder="Share role details, team context, or project scope."
          required
          minLength={24}
          maxLength={800}
        />
      </label>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="submit"
          disabled={isSubmitting}
          className="ai-button px-6 py-3 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-65"
        >
          {isSubmitting ? 'Sending...' : 'Send Message'}
        </button>

        {status.type !== 'idle' ? (
          <p className={`text-sm ${status.type === 'success' ? 'text-emerald-400' : 'text-rose-400'}`}>
            {status.message}
          </p>
        ) : null}
      </div>
    </form>
  );
}

export default function HomePage() {
  const [portfolio, setPortfolio] = useState<PortfolioContent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string>('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isHeaderElevated, setIsHeaderElevated] = useState(false);
  const [adProduct, setAdProduct] = useState('AdCraft AI');
  const [adAudience, setAdAudience] = useState('growth marketers');
  const [adTone, setAdTone] = useState<(typeof TONE_OPTIONS)[number]>('Bold');
  const [adCopy, setAdCopy] = useState('');
  const [chatInput, setChatInput] = useState('');
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([
    { role: 'bot', text: CHATBOT_WELCOME_MESSAGE },
  ]);

  useEffect(() => {
    const controller = new AbortController();

    const loadPortfolio = async () => {
      try {
        const response = await fetch('/api/portfolio', {
          method: 'GET',
          cache: 'no-store',
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error('Could not load portfolio content.');
        }

        const data = (await response.json()) as PortfolioContent;
        setPortfolio(data);
      } catch (error) {
        if (error instanceof Error && error.name !== 'AbortError') {
          setLoadError(error.message);
        }
      } finally {
        setIsLoading(false);
      }
    };

    void loadPortfolio();

    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsHeaderElevated(window.scrollY > 10);
    };

    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
      }
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleGenerateAdCopy = () => {
    setAdCopy(buildAdCopy(adProduct, adAudience, adTone));
  };

  const handleChatSend = () => {
    const userMessage = chatInput.trim();
    if (!userMessage) return;

    const botReply = getChatbotReply(userMessage);
    setChatMessages((previous) => [
      ...previous,
      { role: 'user', text: userMessage },
      { role: 'bot', text: botReply },
    ]);
    setChatInput('');
  };

  const content = portfolio ?? fallbackPortfolio;

  return (
    <div className="ai-shell">
      <AppBar
        position="sticky"
        color="transparent"
        elevation={0}
        sx={{
          top: 0,
          zIndex: (theme) => theme.zIndex.drawer + 1,
          borderBottom: '1px solid',
          borderBottomColor: isHeaderElevated ? 'rgba(34,211,238,0.22)' : 'rgba(148,163,184,0.12)',
          backgroundColor: isHeaderElevated ? 'rgba(5,7,10,0.92)' : 'rgba(5,7,10,0.72)',
          backdropFilter: 'blur(18px)',
          boxShadow: isHeaderElevated ? '0 16px 44px -28px rgba(34,211,238,0.45)' : 'none',
          transition: 'all 220ms ease',
        }}
      >
        <Toolbar disableGutters sx={{ mx: 'auto', minHeight: 64, width: '100%', maxWidth: '72rem', px: { xs: 2.5, md: 4 } }}>
          <Box
            component="a"
            href="#"
            aria-label={`${content.profile.name} home`}
            sx={{
              flexGrow: 1,
              display: 'inline-flex',
              alignItems: 'center',
              textDecoration: 'none',
            }}
          >
            <Box
              component="img"
              src="/subhash-logo.svg"
              alt={`${content.profile.name} logo`}
              sx={{
                height: { xs: 36, md: 44 },
                width: 'auto',
                maxWidth: { xs: 150, md: 190 },
                objectFit: 'contain',
              }}
            />
          </Box>
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
            {navItems.map((item) => (
              <Button
                key={item.label}
                component="a"
                href={item.href}
                download={'isDownload' in item && item.isDownload ? true : undefined}
                onClick={() => setIsMobileMenuOpen(false)}
                sx={{
                  color: '#d4d4d8',
                  textTransform: 'none',
                  fontWeight: 500,
                  '&:hover': {
                    color: '#22d3ee',
                    backgroundColor: 'transparent',
                  },
                }}
              >
                {item.label}
              </Button>
            ))}
          </Box>
          <IconButton
            type="button"
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileMenuOpen}
            onClick={() => setIsMobileMenuOpen((open) => !open)}
            sx={{
              display: { xs: 'inline-flex', md: 'none' },
              border: '1px solid rgba(34,211,238,0.22)',
              borderRadius: 1,
              color: '#e4e4e7',
              backgroundColor: 'rgba(8,13,21,0.92)',
              '&:hover': {
                borderColor: 'rgba(34,211,238,0.48)',
                color: '#22d3ee',
                backgroundColor: 'rgba(8,13,21,1)',
              },
            }}
          >
            {isMobileMenuOpen ? <CloseRoundedIcon fontSize="small" /> : <MenuIcon fontSize="small" />}
          </IconButton>
        </Toolbar>
        <Drawer
          anchor="right"
          open={isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen(false)}
          PaperProps={{
            sx: {
              width: 280,
              backgroundColor: 'rgba(5,7,10,0.98)',
              borderLeft: '1px solid rgba(34,211,238,0.20)',
              color: '#f4f4f5',
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
            <IconButton
              onClick={() => setIsMobileMenuOpen(false)}
              aria-label="Close menu"
              sx={{ color: '#e4e4e7' }}
            >
              <CloseRoundedIcon />
            </IconButton>
          </Box>
          <List sx={{ px: 1, pb: 2 }}>
            {navItems.map((item) => (
              <ListItemButton
                key={`mobile-${item.label}`}
                component="a"
                href={item.href}
                download={'isDownload' in item && item.isDownload ? true : undefined}
                onClick={() => setIsMobileMenuOpen(false)}
                sx={{
                  borderRadius: 1.5,
                  '&:hover': {
                    backgroundColor: 'rgba(34,211,238,0.10)',
                  },
                }}
              >
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ fontWeight: 500 }}
                />
              </ListItemButton>
            ))}
          </List>
        </Drawer>
      </AppBar>
      
      <main className="ai-surface mx-auto flex max-w-6xl flex-col gap-16 px-5 pb-16 pt-10 md:px-8 md:pt-14">
        <motion.section
          initial="hidden"
          animate="visible"
          variants={fadeReveal}
          className="grid items-center gap-10 lg:grid-cols-[1.05fr_0.95fr]"
        >
          <motion.div variants={bioStagger} className="order-2 space-y-6 lg:order-1">
            <motion.div variants={bioItem}>
              <h3 className="ai-kicker inline-flex mb-1 text-xl font-semibold uppercase">
                Subhash Chandra Bose Thangaraj
              </h3>
            </motion.div>
            <motion.div variants={bioItem}>
              <p className="ai-chip px-4 py-2 text-xs font-semibold uppercase">
                Full-Stack, Mobile and AI Integration Engineer
              </p>
            </motion.div>
            <motion.div variants={bioItem}>
              <h1 className="font-heading text-4xl leading-tight font-semibold text-[#ffffff] sm:text-5xl lg:text-6xl">
                Building scalable products with production-ready <span className="ai-text-gradient">AI assistants</span>.
              </h1>
            </motion.div>
            <motion.div variants={bioItem}>
              <p className="max-w-2xl text-base leading-relaxed text-zinc-300 md:text-lg">
                I build end-to-end products with React, Next.js, React Native, Flutter, and Node.js, then integrate
                LLM capabilities using OpenAI, LangChain, and vector search. Strong domain expertise across telecom,
                fintech, e-commerce, healthcare, and insurance.
              </p>
            </motion.div>
            <motion.div variants={bioItem}>
              <div className="flex flex-wrap gap-3">
                <a
                  href="#projects"
                  className="ai-button px-6 py-3 text-sm font-semibold"
                >
                  Explore Projects
                </a>
                <a
                  href="#ai-projects"
                  className="ai-button-secondary px-6 py-3 text-sm font-semibold"
                >
                  Explore AI Apps
                </a>
              </div>
            </motion.div>
            <motion.div variants={bioItem}>
              <motion.div variants={listStagger} className="flex flex-wrap gap-2">
                {[
                  'Next.js and React',
                  'React Native and Flutter',
                  'Node.js and APIs',
                  'Domain Expert',
                  'Cloud and CI/CD',
                  'Prompt Engineering',
                  'RAG and Vector Search',
                  'AI Assistants',
                ].map((item) => (
                  <motion.span
                    key={item}
                    variants={fadeReveal}
                    className="ai-chip px-3 py-1 text-xs font-semibold"
                  >
                    {item}
                  </motion.span>
                ))}
              </motion.div>
            </motion.div>
            <motion.div variants={bioItem}>
              <p className="text-sm text-zinc-400">
                {content.profile.location} · {content.profile.availability}
              </p>
            </motion.div>
            <motion.div variants={bioItem}>
              <div className="flex flex-wrap gap-3">
                {content.profile.social.map((item) => (
                  <Link
                    key={item.label}
                    href={item.href}
                    className="ai-button-secondary min-h-0 px-4 py-2 text-sm font-medium"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </motion.div>
          </motion.div>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeReveal}
            className="order-1 relative z-10 lg:order-2"
          >
            <article className="ai-card ai-float-slow relative z-10 p-3">
              <div className="ai-live-frame pointer-events-auto relative z-10 aspect-video w-full overflow-hidden rounded-lg border border-cyan-300/20 bg-black">
                <video
                  className="relative z-10 h-full w-full object-cover"
                  src={HERO_VIDEO_SRC}
                  poster={HERO_VIDEO_POSTER}
                  autoPlay
                  muted
                  loop
                  controls
                  playsInline
                  preload="metadata"
                  tabIndex={0}
                  aria-label="Subhash coding session video"
                />
              </div>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs font-semibold tracking-[0.13em] text-zinc-300 uppercase">
                  Live Coding Showcase
                </p>
                <p className="ai-chip px-3 py-1 text-[11px] font-semibold uppercase">
                  Full-stack workflow
                </p>
              </div>
            </article>
          </motion.div>
        </motion.section>

        <motion.div
          initial="hidden"
          animate="visible"
          variants={listStagger}
          className="grid gap-4 sm:grid-cols-3"
        >
          {content.profile.metrics.map((metric) => (
            <motion.article
              key={metric.label}
              variants={sectionReveal}
              className="ai-card p-6"
            >
              <p className="text-xs tracking-[0.18em] text-zinc-500 uppercase">{metric.label}</p>
              <p className="ai-metric font-heading mt-3 text-4xl font-semibold text-[#ffffff]">
                {metric.value}
              </p>
              <p className="mt-2 text-sm text-zinc-400">{metric.detail}</p>
            </motion.article>
          ))}
        </motion.div>

        <motion.section
          id="projects"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={sectionReveal}
          className="space-y-5"
        >
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="ai-kicker text-xs font-semibold uppercase">Featured Projects</p>
              <h2 className="font-heading mt-2 text-3xl font-semibold text-[#ffffff] sm:text-4xl">
                Products shipped across industries
              </h2>
            </div>
          </div>

          <motion.div
            variants={listStagger}
            className="grid gap-5 lg:grid-cols-2"
          >
            {content.projects.map((project) => (
              <motion.article
                key={project.name}
                variants={sectionReveal}
                className="ai-card p-6"
              >
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-heading text-2xl font-semibold text-[#f4f4f5]">
                    {project.name}
                  </h3>
                  <p className="ai-badge px-3 py-1 text-xs font-semibold">
                    {project.highlight}
                  </p>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-zinc-300">{project.summary}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {project.stack.map((tech) => (
                    <span
                      key={`${project.name}-${tech}`}
                      className="ai-chip px-3 py-1 text-xs font-medium"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
                <div className="mt-5 flex gap-3 text-sm font-semibold">
                  <Link href={project.url} className="text-cyan-200 hover:text-cyan-300">
                    Live Product
                  </Link>
                  <Link href={project.repo} className="text-zinc-400 hover:text-violet-200">
                    Source
                  </Link>
                </div>
              </motion.article>
            ))}
          </motion.div>
        </motion.section>

        <motion.section
          id="ai-projects"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={sectionReveal}
          className="space-y-5"
        >
          <div>
            <p className="ai-kicker text-xs font-semibold uppercase">AI Projects Playground</p>
            <h2 className="font-heading mt-2 text-3xl font-semibold text-[#ffffff] sm:text-4xl">
              Explore and play with AI experiences
            </h2>
          </div>

          <motion.div variants={listStagger} className="grid gap-5 lg:grid-cols-2">
            {content.aiProjects.map((project) => (
              <motion.article
                key={project.id}
                variants={sectionReveal}
                className="ai-card p-6"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h3 className="font-heading text-2xl font-semibold text-[#f4f4f5]">
                    {project.name}
                  </h3>
                  <p className="ai-badge ai-badge-purple px-3 py-1 text-xs font-semibold">
                    {project.status}
                  </p>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-zinc-300">{project.summary}</p>
                <p className="mt-3 text-xs font-semibold tracking-[0.12em] text-zinc-400 uppercase">
                  {project.highlight}
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {project.stack.map((tech) => (
                    <span
                      key={`${project.id}-${tech}`}
                      className="ai-chip px-3 py-1 text-xs font-medium"
                    >
                      {tech}
                    </span>
                  ))}
                </div>

                <div className="mt-5 flex gap-3 text-sm font-semibold">
                  <Link href={project.demoUrl} className="text-cyan-200 hover:text-cyan-300">
                    Open Demo
                  </Link>
                  <Link href={project.repoUrl} className="text-zinc-400 hover:text-violet-200">
                    Source
                  </Link>
                </div>

                {project.id === 'adcraft-ai' ? (
                  <div className="ai-terminal mt-5 p-4">
                    <p className="ai-kicker text-xs font-semibold uppercase">Try AdCraft</p>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      <input
                        value={adProduct}
                        onChange={(event) => setAdProduct(event.target.value)}
                        placeholder="Product name"
                        className="ai-input px-3 py-2 text-sm outline-none"
                      />
                      <input
                        value={adAudience}
                        onChange={(event) => setAdAudience(event.target.value)}
                        placeholder="Audience"
                        className="ai-input px-3 py-2 text-sm outline-none"
                      />
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {TONE_OPTIONS.map((tone) => (
                        <button
                          key={tone}
                          type="button"
                          onClick={() => setAdTone(tone)}
                          className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                            adTone === tone
                              ? 'bg-cyan-300 text-slate-950'
                              : 'border border-slate-400/20 bg-slate-950/70 text-zinc-300 hover:border-cyan-300/40'
                          }`}
                        >
                          {tone}
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={handleGenerateAdCopy}
                      className="ai-button mt-3 min-h-0 px-4 py-2 text-xs font-semibold"
                    >
                      Generate Sample Copy
                    </button>
                    {adCopy ? (
                      <p className="mt-3 rounded-lg border border-cyan-300/15 bg-slate-950/70 p-3 text-sm leading-relaxed text-zinc-300">
                        {adCopy}
                      </p>
                    ) : null}
                  </div>
                ) : null}

                {project.id === 'chat-bots' ? (
                  <div className="ai-terminal mt-5 p-4">
                    <p className="ai-kicker text-xs font-semibold uppercase">Try Chat Bot</p>
                    <div className="mt-3 max-h-44 space-y-2 overflow-y-auto pr-1">
                      {chatMessages.map((message, index) => (
                        <div
                          key={`${message.role}-${index}`}
                          className={`rounded-lg px-3 py-2 text-sm ${
                            message.role === 'user'
                              ? 'ai-message-user'
                              : 'ai-message-bot'
                          }`}
                        >
                          {message.text}
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 flex gap-2">
                      <input
                        value={chatInput}
                        onChange={(event) => setChatInput(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            handleChatSend();
                          }
                        }}
                        placeholder="Ask about AI integration..."
                        className="ai-input flex-1 px-3 py-2 text-sm outline-none"
                      />
                      <button
                        type="button"
                        onClick={handleChatSend}
                        className="ai-button min-h-0 px-4 py-2 text-xs font-semibold"
                      >
                        Send
                      </button>
                    </div>
                  </div>
                ) : null}
              </motion.article>
            ))}
          </motion.div>
        </motion.section>

        <div className="grid gap-8 lg:grid-cols-[1fr_1fr]">
          <motion.section
            id="services"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={sectionReveal}
            className="space-y-5"
          >
            <p className="ai-kicker text-xs font-semibold uppercase">Core Expertise</p>
            <h2 className="font-heading text-3xl font-semibold text-[#ffffff] sm:text-4xl">
              Architecture, delivery, leadership
            </h2>
            <div className="space-y-3">
              {content.services.map((service) => (
                <article
                  key={service.title}
                  className="ai-card p-5"
                >
                  <h3 className="font-heading text-xl font-semibold text-[#f4f4f5]">{service.title}</h3>
                  <p className="mt-2 text-sm text-zinc-300">{service.description}</p>
                </article>
              ))}
            </div>
          </motion.section>

          <motion.section
            id="experience"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={sectionReveal}
            className="space-y-5"
          >
            <p className="ai-kicker text-xs font-semibold uppercase">Experience</p>
            <h2 className="font-heading text-3xl font-semibold text-[#ffffff] sm:text-4xl">
              Teams, products, outcomes
            </h2>
            <div className="space-y-4">
              {content.experience.map((item) => (
                <article
                  key={`${item.company}-${item.role}`}
                  className="ai-card p-5"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="font-heading text-xl font-semibold text-[#f4f4f5]">{item.role}</h3>
                    <p className="text-xs font-semibold tracking-[0.14em] text-zinc-500 uppercase">{item.period}</p>
                  </div>
                  <p className="mt-1 text-sm font-medium text-cyan-100">{item.company}</p>
                  <p className="mt-2 text-sm text-zinc-300">{item.impact}</p>
                </article>
              ))}
            </div>
          </motion.section>
        </div>

        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={sectionReveal}
          className="space-y-5"
        >
          <p className="ai-kicker text-xs font-semibold uppercase">Technology Stack</p>
          <div className="flex flex-wrap gap-3">
            {content.skills.map((skill) => (
              <span
                key={skill}
                className="ai-chip px-4 py-2 text-sm font-semibold"
              >
                {skill}
              </span>
            ))}
          </div>
        </motion.section>

        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={sectionReveal}
          className="space-y-5"
        >
          <p className="ai-kicker text-xs font-semibold uppercase">Impact Highlights</p>
          <div className="grid gap-4 md:grid-cols-2">
            {content.testimonials.map((testimonial) => (
              <article
                key={`${testimonial.name}-${testimonial.role}`}
                className="ai-card p-6"
              >
                <p className="text-base leading-relaxed text-zinc-300">{testimonial.quote}</p>
                <p className="mt-4 font-heading text-lg font-semibold text-[#f4f4f5]">{testimonial.name}</p>
                <p className="text-sm text-zinc-500">{testimonial.role}</p>
              </article>
            ))}
          </div>
        </motion.section>

        <motion.section
          id="contact"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={sectionReveal}
          className="ai-panel grid gap-8 p-6 md:grid-cols-[0.9fr_1.1fr] md:p-10"
        >
          <div className="space-y-4">
            <p className="ai-kicker text-xs font-semibold uppercase">Let&apos;s Connect</p>
            <h2 className="font-heading text-3xl font-semibold text-[#ffffff] sm:text-4xl">
              Open to leadership roles and high-impact builds.
            </h2>
            <p className="text-sm leading-relaxed text-zinc-300">
              This form is backed by a live Next.js API endpoint with validation and throttling.
              Share your role or project details and I will respond quickly.
            </p>
            <div className="ai-terminal p-4 text-sm text-zinc-300">
              <p>Response time: within 24 hours</p>
              <p>Timezone overlap: GST, IST, CET</p>
              <p>Location: Dubai, UAE</p>
            </div>
          </div>

          <ContactForm />
        </motion.section>

        {isLoading ? (
          <p className="text-sm text-zinc-500">Loading live portfolio content...</p>
        ) : null}
        {loadError ? (
          <p className="text-sm text-rose-400">{loadError} Showing fallback content.</p>
        ) : null}
      </main>
      <footer className="ai-surface border-t border-cyan-300/15 bg-slate-950/75">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-center px-5 py-5 md:px-8">
          <p className="text-center text-xs tracking-[0.12em] text-zinc-400 uppercase">
            Copyright © {new Date().getFullYear()} {content.profile.name}. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
